import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { DynamicFormService } from '../../../../services/dynamic-form.service';
import {
  DynamicFormField,
  DynamicFormStripeProduct,
} from '../../../../interfaces/dynamic-form';

interface ProductCard {
  key: string;
  name: string;
  description: string | null;
  amount: number; // acconto
  quotaTotale: number; // full price
  isFreeOption: boolean;
  ordine: number;
}

export interface ProductPriceInfo {
  acconto: number;
  quotaTotale: number;
}

@Component({
  selector: 'app-dynamic-product-cards',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './dynamic-product-cards.component.html',
  styleUrl: './dynamic-product-cards.component.scss',
})
export class DynamicProductCardsComponent implements OnInit, OnChanges {
  private formService = inject(DynamicFormService);

  @Input() field!: DynamicFormField;
  @Input() value: unknown;
  @Output() valueChange = new EventEmitter<unknown>();
  @Output() priceChange = new EventEmitter<ProductPriceInfo>();

  cards: ProductCard[] = [];
  loading = false;
  loadError = '';
  showPrices = true;

  get isInclusi(): boolean {
    return this.field.modalita === 'inclusi';
  }

  ngOnInit(): void {
    this.showPrices = this.field.mostraPrezzi !== false;
    this.loadStripeProducts();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['value'] && !changes['value'].firstChange) {
      this.emitPriceChange();
    }
  }

  isSelected(key: string): boolean {
    if (this.field.selezioneMultipla) {
      return (
        Array.isArray(this.value) && (this.value as string[]).includes(key)
      );
    }
    return this.value === key;
  }

  toggleCard(key: string): void {
    if (this.isInclusi) return; // Can't toggle in inclusi mode

    if (this.field.selezioneMultipla) {
      const current = Array.isArray(this.value)
        ? [...(this.value as string[])]
        : [];
      const idx = current.indexOf(key);
      if (idx >= 0) {
        current.splice(idx, 1);
      } else {
        current.push(key);
      }
      this.valueChange.emit(current.length > 0 ? current : undefined);
    } else {
      const newValue = this.value === key ? undefined : key;
      this.valueChange.emit(newValue);
    }
    setTimeout(() => this.emitPriceChange(), 0);
  }

  formatPrice(cents: number): string {
    return `\u20AC${(cents / 100).toFixed(2)}`;
  }

  private loadStripeProducts(): void {
    if (!this.field.metadataValore) return;

    this.loading = true;
    this.formService
      .getStripeProducts(
        'evento',
        this.field.metadataValore,
        this.field.categoriaFiltro,
      )
      .subscribe({
        next: (res) => {
          this.loading = false;
          if (res.success) {
            this.cards = res.products
              .map((p) => ({
                key: p.key,
                name: p.name,
                description: p.metadata?.['descrizione'] || p.description,
                amount: p.amount,
                quotaTotale: p.quotaTotale,
                isFreeOption: false,
                ordine: parseInt(p.metadata?.['ordine'] || '99', 10),
              }))
              .sort((a, b) => a.ordine - b.ordine);

            if (!this.isInclusi) {
              this.appendFreeOptions();
              this.autoSelectSingle();
            } else {
              this.autoSelectAll();
            }
            this.emitPriceChange();
          }
        },
        error: () => {
          this.loading = false;
          this.loadError = 'Errore nel caricamento delle opzioni.';
        },
      });
  }

  private appendFreeOptions(): void {
    if (this.field.opzioniGratuite?.length) {
      for (const opt of this.field.opzioniGratuite) {
        this.cards.push({
          key: opt.valore,
          name: opt.etichetta,
          description: opt.descrizione || null,
          amount: 0,
          quotaTotale: 0,
          isFreeOption: true,
          ordine: 900 + this.cards.length,
        });
      }
    }
  }

  /** Auto-select all products (inclusi mode) */
  private autoSelectAll(): void {
    const allKeys = this.cards.map((c) => c.key);
    this.valueChange.emit(allKeys);
  }

  /** Auto-select if preseleziona is enabled (selezione mode) */
  private autoSelectSingle(): void {
    if (!this.field.preseleziona || this.value) return;

    if (this.field.selezioneMultipla) {
      // Select all non-free products
      const paidKeys = this.cards
        .filter((c) => !c.isFreeOption)
        .map((c) => c.key);
      if (paidKeys.length > 0) this.valueChange.emit(paidKeys);
    } else if (this.cards.length === 1) {
      this.valueChange.emit(this.cards[0].key);
    }
  }

  private emitPriceChange(): void {
    let acconto = 0;
    let quotaTotale = 0;

    if (this.isInclusi) {
      // All products are always selected
      for (const card of this.cards) {
        acconto += card.amount;
        quotaTotale += card.quotaTotale;
      }
    } else {
      const selected = this.field.selezioneMultipla
        ? Array.isArray(this.value)
          ? (this.value as string[])
          : []
        : this.value
          ? [this.value as string]
          : [];

      for (const key of selected) {
        const card = this.cards.find((c) => c.key === key);
        if (card && !card.isFreeOption) {
          acconto += card.amount;
          quotaTotale += card.quotaTotale;
        }
      }
    }
    this.priceChange.emit({ acconto, quotaTotale });
  }
}
